/**
 * Typed client for the nuffsaid HTTP API.
 *
 * Every call attaches the Cognito ID token as a bearer (`Authorization: Bearer
 * <idToken>`): the API's JWT authorizer requires the ID token, not the access
 * token (see SECURITY.md). The token is fetched fresh per request via the
 * injected `getIdToken` so an expired token is transparently refreshed. On a 401
 * the caller's `onUnauthorized` runs (clear the session and route to sign-in).
 */
import type {
  Cadence,
  Issue,
  IssueEditInput,
  MeResponse,
  Newsletter,
  NewsletterConfig,
  NewsletterInput,
  SectionSelection,
  SeedInstructionsResult,
  SentIssueListResponse,
  Subscriber,
  SubscriberImportResult,
  SubscriberListResponse,
  SubscriberUploadContentType,
  SubscriberUploadUrlResponse,
  SuggestedSection,
  Treatment,
  UploadFileKind,
} from '@nuff/types';

import { config } from './config';

// The section-discovery shapes are owned by @nuff/types (one source of truth);
// re-exported here so callers keep a single `./api` import surface.
export {
  TREATMENTS,
  CHANNELS,
  CHANNEL_DISPLAY_NAMES,
  sourceDisplayName,
  sourceUrl,
} from '@nuff/types';
export { CADENCES } from '@nuff/types';
export type {
  Cadence,
  Newsletter,
  NewsletterConfig,
  LikedNewsletter,
  Treatment,
  Channel,
  SourceType,
  SectionStatus,
  SourceRef,
  QueryStreamSourceRef,
  SiteFeedSourceRef,
  SourceMetrics,
  SuggestedSection,
  SectionSelection,
  IssuePreview,
  IssuePreviewSection,
  IssuePreviewItem,
  Issue,
  ContentBlock,
  ContentBlockRole,
  ContentBlockStatus,
  ContentSourceRef,
  IssueStatus,
  IssueEditInput,
  ContentBlockEdit,
  Subscriber,
  SubscriberStatus,
  SubscriberImportResult,
  SubscriberListResponse,
  SentIssue,
  SentIssueListResponse,
  SeededInstruction,
  MissedLikedNewsletter,
  SeedInstructionsResult,
} from '@nuff/types';

/** An API request failed. `status` is the HTTP status (0 for network errors). */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Wiring the client needs from the auth layer. */
export interface ApiDeps {
  /** Resolve a fresh, valid Cognito ID token. */
  readonly getIdToken: () => Promise<string>;
  /** Called when the API rejects the token (401): clear session, route to sign-in. */
  readonly onUnauthorized: () => void;
}

async function request<T>(deps: ApiDeps, path: string, init: RequestInit = {}): Promise<T> {
  let token: string;
  try {
    token = await deps.getIdToken();
  } catch {
    deps.onUnauthorized();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }

  if (response.status === 401) {
    deps.onUnauthorized();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  if (!response.ok) {
    const message = await readError(response);
    throw new ApiError(message, response.status);
  }

  return (await response.json()) as T;
}

/**
 * The calm, non-technical message shown when the monthly AI budget is reached. The
 * server sends this verbatim on a 503 from an AI route; we ALSO map any AI-route 503 to
 * it client-side so the studio/inspector always shows an honest "paused this month" line
 * rather than a raw failure, even if the server body ever differs. Kept in sync with the
 * server's BEDROCK_BUDGET_PAUSED_MESSAGE.
 */
export const AI_PAUSED_MESSAGE =
  "Our AI is paused for this month because we reached this month's usage limit. It resumes next month.";

/**
 * Wrap an AI-route call so the real-time Bedrock budget stop (a 503) surfaces as the
 * calm {@link AI_PAUSED_MESSAGE} rather than a jargon-y failure. Only the AI routes ever
 * return 503, so this never masks an unrelated error.
 */
async function withAiBudgetMessage<T>(work: Promise<T>): Promise<T> {
  try {
    return await work;
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 503) {
      throw new ApiError(AI_PAUSED_MESSAGE, 503);
    }
    throw caught;
  }
}

/** Best-effort extraction of a human-readable error from a failed response. */
async function readError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'message' in body) {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
    }
  } catch {
    // Non-JSON body; fall through to the generic message.
  }
  return `Request failed (${String(response.status)}).`;
}

/** `GET /me`: the signed-in user's tenant plus their newsletters. */
export function getMe(deps: ApiDeps): Promise<MeResponse> {
  return request<MeResponse>(deps, '/me');
}

/** `GET /newsletters`: the tenant's newsletters. */
export function listNewsletters(deps: ApiDeps): Promise<Newsletter[]> {
  return request<Newsletter[]>(deps, '/newsletters');
}

/** `POST /newsletters`: create a newsletter and return the created record. */
export function createNewsletter(deps: ApiDeps, input: NewsletterInput): Promise<Newsletter> {
  return request<Newsletter>(deps, '/newsletters', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * `PATCH /newsletters/{id}`: update any subset of a newsletter's editable config
 * (every field in the @nuff/types config source of truth: topics, tagline,
 * purpose, audience, voice, likedNewsletters, authorName, authorBio, socials,
 * fromName, replyEmail, location, timezone) and return the updated newsletter. Only the
 * keys present in `partial` are written server-side.
 */
export function updateNewsletterConfig(
  deps: ApiDeps,
  newsletterId: string,
  partial: NewsletterConfig,
): Promise<Newsletter> {
  return request<Newsletter>(deps, `/newsletters/${newsletterId}`, {
    method: 'PATCH',
    body: JSON.stringify(partial),
  });
}

/**
 * `DELETE /newsletters/{id}`: permanently delete a newsletter (and, server-side,
 * its suggested sections and any subdomain claim). Returns `{ deleted: true }`.
 */
export function deleteNewsletter(
  deps: ApiDeps,
  newsletterId: string,
): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(deps, `/newsletters/${newsletterId}`, {
    method: 'DELETE',
  });
}

/**
 * `POST /newsletters/{id}/discover`: run source discovery for a newsletter and
 * return the freshly suggested sections. This can take ~10-20s. When `topics`
 * are passed they (re)set the newsletter's topics before discovering, so a
 * newsletter that has none can be given some.
 */
export function discoverSources(
  deps: ApiDeps,
  newsletterId: string,
  topics?: readonly string[],
): Promise<{ sections: SuggestedSection[] }> {
  const init: RequestInit = { method: 'POST' };
  if (topics !== undefined && topics.length > 0) {
    init.body = JSON.stringify({ topics });
  }
  return withAiBudgetMessage(
    request<{ sections: SuggestedSection[] }>(deps, `/newsletters/${newsletterId}/discover`, init),
  );
}

/**
 * `GET /newsletters/{id}/sections`: the newsletter's currently persisted
 * suggested sections (without re-running discovery).
 */
export function listSections(
  deps: ApiDeps,
  newsletterId: string,
): Promise<{ sections: SuggestedSection[] }> {
  return request<{ sections: SuggestedSection[] }>(deps, `/newsletters/${newsletterId}/sections`);
}

/**
 * `PATCH /newsletters/{id}/sections`: persist a batch of section selection
 * changes (select / leave suggested / dismiss, with an optional running order).
 * Returns the newsletter's fresh section list so the caller can re-sync.
 */
export function updateSections(
  deps: ApiDeps,
  newsletterId: string,
  updates: readonly SectionSelection[],
): Promise<{ sections: SuggestedSection[] }> {
  return request<{ sections: SuggestedSection[] }>(deps, `/newsletters/${newsletterId}/sections`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  });
}

/** The result of a propose-structure call: the fresh sections plus the kept/set-aside split. */
export interface ProposeStructureResult {
  readonly sections: SuggestedSection[];
  /** How many sections were kept in the running order. */
  readonly kept?: number;
  /** How many live sections were intentionally set aside (left available, not in order). */
  readonly setAside?: number;
}

/**
 * `POST /newsletters/{id}/propose-structure`: ask our AI to arrange the already
 * discovered sections into an ordered, curated running order, persisting the
 * chosen ones as selected (with an order) and the rest as suggested. Returns the
 * fresh section list plus a kept/set-aside count so the UI can show the split.
 * Can take a few seconds (it calls a model).
 */
export function proposeStructure(
  deps: ApiDeps,
  newsletterId: string,
): Promise<ProposeStructureResult> {
  return withAiBudgetMessage(
    request<ProposeStructureResult>(deps, `/newsletters/${newsletterId}/propose-structure`, {
      method: 'POST',
    }),
  );
}

/**
 * `GET /newsletters/{id}/issue`: the newsletter's PERSISTED draft issue (subject +
 * prose blocks). The backend lazily assembles and persists the draft on the first
 * read (so the studio "opens already drafted"), then returns it as a pure read on
 * subsequent calls: no re-bill unless the owner regenerates. A 400 comes back when
 * there is nothing to assemble yet (no topics and no sections).
 */
export function getIssue(deps: ApiDeps, newsletterId: string): Promise<Issue> {
  return request<Issue>(deps, `/newsletters/${newsletterId}/issue`);
}

/**
 * `POST /newsletters/{id}/issue/generate`: kick off an ASYNC (off-request)
 * reassembly of the draft issue from the newsletter's CURRENTLY chosen sections. The
 * heavy assemble + Sonnet write runs in the generator Lambda, so this returns fast
 * (HTTP 202) with an issue whose `status` is `generating`; the caller then polls
 * {@link getIssue} until the status flips to a finished issue. When the backend can
 * satisfy the request without a fresh generation it may instead return the ready
 * issue directly, so callers key off the returned `status`, not the presence of a
 * generation. Fire it only when the curation actually changes (or on Regenerate).
 */
export function generateIssue(deps: ApiDeps, newsletterId: string): Promise<Issue> {
  return request<Issue>(deps, `/newsletters/${newsletterId}/issue/generate`, { method: 'POST' });
}

/**
 * `PATCH /newsletters/{id}/issue`: apply a bounded set of edits (a new subject
 * and/or per-block rename/rewrite/reorder/keep/dismiss) to the draft and return the
 * fresh issue. Wired now for P1.5's editing affordances; the P1.4 shell is
 * read-only and does not call it.
 */
export function patchIssue(
  deps: ApiDeps,
  newsletterId: string,
  edits: IssueEditInput,
): Promise<Issue> {
  return request<Issue>(deps, `/newsletters/${newsletterId}/issue`, {
    method: 'PATCH',
    body: JSON.stringify(edits),
  });
}

/**
 * `PATCH /newsletters/{id}/sections/{sectionId}`: persist the owner's editable AI
 * INSTRUCTION for one section (a plain-language steer for how our AI writes it). An
 * empty string CLEARS it. Returns the newsletter's fresh section list so the caller
 * can re-sync. The instruction only shapes writing on the next regenerate; it never
 * invents facts.
 */
export function setSectionInstruction(
  deps: ApiDeps,
  newsletterId: string,
  sectionId: string,
  instruction: string,
): Promise<{ sections: SuggestedSection[] }> {
  return request<{ sections: SuggestedSection[] }>(
    deps,
    `/newsletters/${newsletterId}/sections/${sectionId}`,
    { method: 'PATCH', body: JSON.stringify({ instruction }) },
  );
}

/**
 * `PATCH /newsletters/{id}/sections/{sectionId}`: persist the owner's chosen TREATMENT
 * for one section (the honest "how much AI touches the source" knob). Shares the section
 * PATCH endpoint with the instruction lever. Returns the newsletter's fresh section list
 * so the caller can re-sync. The treatment only changes writing on the next regenerate.
 */
export function setSectionTreatment(
  deps: ApiDeps,
  newsletterId: string,
  sectionId: string,
  treatment: Treatment,
): Promise<{ sections: SuggestedSection[] }> {
  return request<{ sections: SuggestedSection[] }>(
    deps,
    `/newsletters/${newsletterId}/sections/${sectionId}`,
    { method: 'PATCH', body: JSON.stringify({ treatment }) },
  );
}

/**
 * `POST /newsletters/{id}/sections/seed-instructions` (opt-in, E2.1): ask our AI to
 * DRAFT a starter AI instruction for each writer-driven section from the newsletters
 * the owner loves. Returns the drafts (each attributed to the liked newsletter it came
 * from) WITHOUT persisting, so the owner reviews and keeps/edits/dismisses each; plus a
 * `missed` list naming any liked newsletter we could not read (so we never imply we
 * studied something we didn't). Takes a few seconds (it fetches liked newsletters and
 * calls our AI), so callers disable the trigger while it is in flight.
 */
export function seedInstructions(
  deps: ApiDeps,
  newsletterId: string,
): Promise<SeedInstructionsResult> {
  return withAiBudgetMessage(
    request<SeedInstructionsResult>(
      deps,
      `/newsletters/${newsletterId}/sections/seed-instructions`,
      { method: 'POST' },
    ),
  );
}

/**
 * `POST /newsletters/{id}/issue/blocks/{blockId}/regenerate`: re-render just ONE
 * section's prose SYNCHRONOUSLY, from fresh material and the section's AI instruction,
 * without rebuilding the rest of the issue. When `instruction` is passed it is saved
 * first (so a click always uses the just-typed text), otherwise the persisted one
 * steers. Returns the full updated Issue with the SAME block ids + issueId, so the
 * caller settles it in place (the inspector selection stays put). Takes a few seconds
 * (it re-fetches live headlines and makes one writer call), so callers disable the
 * button while it is in flight. On a soft miss the returned issue keeps the old body.
 */
export function regenerateBlock(
  deps: ApiDeps,
  newsletterId: string,
  blockId: string,
  instruction?: string,
): Promise<Issue> {
  return withAiBudgetMessage(
    request<Issue>(deps, `/newsletters/${newsletterId}/issue/blocks/${blockId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ instruction }),
    }),
  );
}

/**
 * `POST /newsletters/{id}/issue/refresh-opening`: resync the issue's subject + intro to
 * the CURRENT blocks after a per-section regenerate (which rewrites one block body only
 * and leaves the opening stale). Re-runs the editor-spine and patches subject + intro
 * body only; returns the full updated Issue with the SAME block ids + issueId, so the
 * caller settles it in place. Takes a couple of seconds (one model call). On a soft miss
 * the returned issue keeps the existing subject + intro.
 */
export function refreshOpening(deps: ApiDeps, newsletterId: string): Promise<Issue> {
  return withAiBudgetMessage(
    request<Issue>(deps, `/newsletters/${newsletterId}/issue/refresh-opening`, {
      method: 'POST',
    }),
  );
}

/** The outcome of a send-test: the SES message id for the one delivered copy. */
export interface SendTestResult {
  readonly messageId: string;
}

/**
 * The acknowledgement of a real send: the fresh sendId and the frozen issueId, with
 * status `queued`. The fan-out to CONFIRMED subscribers happens asynchronously, so
 * the response carries NO per-recipient status and NO recipient count (surface the
 * confirmed count the UI already knows instead).
 */
export interface SendResult {
  readonly sendId: string;
  readonly issueId: string;
  readonly status: 'queued';
}

/**
 * Map a raw send failure onto a friendly, brand-voice message keyed on the backend
 * status code. 404 (no such newsletter), 413 (snapshot too large), and 422 (a
 * per-send delivery rejection) get a clearer line; a 400 (empty draft / invalid
 * address) already carries a friendly server message, so it (and anything else, e.g.
 * a network error) passes through unchanged.
 */
function friendlySendError(caught: unknown): ApiError {
  if (caught instanceof ApiError) {
    switch (caught.status) {
      case 404:
        return new ApiError('We could not find this newsletter. It may have been deleted.', 404);
      case 413:
        return new ApiError(
          'This issue is too large to send. Trim its content and try again.',
          413,
        );
      case 422:
        return new ApiError('We could not send that just now. Please try again.', 422);
      default:
        return caught;
    }
  }
  return new ApiError('Could not send just now. Please try again.', 0);
}

/**
 * `POST /newsletters/{id}/issue/send-test`: render the CURRENT persisted draft and
 * send ONE copy to the authenticated owner's own email (from the validated JWT), so
 * the owner can see a real issue in their inbox before sending it out. Returns the
 * delivered message id; maps 404/413/422 (and the empty-draft 400) to a friendly
 * message.
 */
export async function sendIssueTest(deps: ApiDeps, newsletterId: string): Promise<SendTestResult> {
  try {
    return await request<SendTestResult>(deps, `/newsletters/${newsletterId}/issue/send-test`, {
      method: 'POST',
    });
  } catch (caught) {
    throw friendlySendError(caught);
  }
}

/**
 * `POST /newsletters/{id}/issue/send`: mint a fresh sendId, snapshot the current
 * draft, and enqueue the fan-out to all CONFIRMED subscribers. Returns FAST with the
 * sendId + `queued` status (the dispatcher/sender Lambdas do the async fan-out), so
 * the response carries no recipient count: show the confirmed count the UI already
 * holds. Maps 404/413 (and the empty-draft 400) to a friendly message.
 */
export async function sendIssue(deps: ApiDeps, newsletterId: string): Promise<SendResult> {
  try {
    return await request<SendResult>(deps, `/newsletters/${newsletterId}/issue/send`, {
      method: 'POST',
    });
  } catch (caught) {
    throw friendlySendError(caught);
  }
}

/**
 * The result of one refine-chat turn: our AI's reply, a human "what changed"
 * summary (one line per applied edit), and the fresh newsletter + sections after
 * the edits so the rest of the view can re-sync to the applied changes.
 */
export interface RefineResult {
  readonly reply: string;
  readonly changed: string[];
  readonly newsletter: Newsletter;
  readonly sections: SuggestedSection[];
}

/**
 * `POST /newsletters/{id}/refine`: send the owner's plain-language message to our
 * AI, which replies and applies a bounded set of structured edits to the config
 * and sections server-side. Returns the reply, a "what changed" summary, and the
 * fresh newsletter + sections. Can take a few seconds (it calls a model, and a
 * message may trigger discovery), so callers disable the input while it is in flight.
 */
export function refineNewsletter(
  deps: ApiDeps,
  newsletterId: string,
  message: string,
): Promise<RefineResult> {
  return withAiBudgetMessage(
    request<RefineResult>(deps, `/newsletters/${newsletterId}/refine`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  );
}

/**
 * `POST /newsletters/{id}/schedule`: move the newsletter into its `scheduled`
 * lifecycle state with the chosen cadence. The server sets status='scheduled' and
 * computes the first-issue date (`nextIssueAt`). This records intent only, real
 * automated delivery is a later phase, so nothing is actually sent yet.
 */
export function scheduleNewsletter(
  deps: ApiDeps,
  newsletterId: string,
  cadence: Cadence,
): Promise<Newsletter> {
  return request<Newsletter>(deps, `/newsletters/${newsletterId}/schedule`, {
    method: 'POST',
    body: JSON.stringify({ cadence }),
  });
}

/**
 * `GET /newsletters/{id}/subscribers`: the newsletter's subscriber list plus its
 * count. Owner-added/imported readers are `confirmed` and sendable at once (the owner
 * asserts consent); only a PUBLIC self-signup is `pending` until they click the
 * double-opt-in confirmation link, at which point they become `confirmed`.
 */
export function listSubscribers(
  deps: ApiDeps,
  newsletterId: string,
): Promise<SubscriberListResponse> {
  return request<SubscriberListResponse>(deps, `/newsletters/${newsletterId}/subscribers`);
}

/**
 * `GET /newsletters/{id}/sent-issues`: the newsletter's sent-issue history, newest
 * first, bounded to the most recent rows. A read-only record of what actually went
 * out (subject + when + how many readers + issue number). This is a plain tenant read
 * (no billed model/discovery work), so callers can safely load it on mount.
 */
export function listSentIssues(
  deps: ApiDeps,
  newsletterId: string,
): Promise<SentIssueListResponse> {
  return request<SentIssueListResponse>(deps, `/newsletters/${newsletterId}/sent-issues`);
}

/**
 * `POST /newsletters/{id}/subscribers`: add ONE subscriber by email. The server
 * lowercases/trims and rejects a duplicate with a 409 (surfaced as an ApiError). An
 * OWNER add lands the reader `confirmed` and sendable at once (the owner asserts they
 * opted in); NO confirmation email is sent. Double opt-in applies only to public
 * self-signups through the newsletter's own page.
 */
export function addSubscriber(
  deps: ApiDeps,
  newsletterId: string,
  email: string,
): Promise<Subscriber> {
  return request<Subscriber>(deps, `/newsletters/${newsletterId}/subscribers`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * `POST /newsletters/{id}/subscribers/import`: bulk-import RAW pasted lines. The
 * server categorizes every non-blank line into a reconcilable breakdown -
 * `{ added, skippedExisting, skippedDuplicateInPaste, invalid, invalidLines? }` -
 * so `added + skippedExisting + skippedDuplicateInPaste + invalid` equals what was
 * pasted. Send the lines verbatim (do NOT pre-validate or de-dup) so the server's
 * counts and echoed invalid lines line up with exactly what the owner entered.
 */
export function importSubscribers(
  deps: ApiDeps,
  newsletterId: string,
  emails: readonly string[],
): Promise<SubscriberImportResult> {
  return request<SubscriberImportResult>(deps, `/newsletters/${newsletterId}/subscribers/import`, {
    method: 'POST',
    body: JSON.stringify({ emails }),
  });
}

/**
 * Pick the declared upload Content-Type and the parser `kind` for a chosen file.
 * The file EXTENSION wins (`.csv` -> a CSV, `.txt` -> plain text) because a
 * browser's reported MIME type for a CSV is unreliable (often blank, or the
 * spreadsheet `application/vnd.ms-excel`); we only fall back to the reported MIME
 * type when the name gives us nothing. Anything else is rejected up front with a
 * friendly error so we never mint an upload slot for a file we cannot import.
 */
export function resolveUploadType(file: File): {
  readonly contentType: SubscriberUploadContentType;
  readonly kind: UploadFileKind;
} {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    return { contentType: 'text/csv', kind: 'csv' };
  }
  if (name.endsWith('.txt')) {
    return { contentType: 'text/plain', kind: 'text' };
  }
  // No telling extension: trust the browser's MIME type, mapping the spreadsheet
  // label commonly attached to an exported .csv to a CSV parse.
  const mime = file.type;
  if (mime === 'text/csv' || mime === 'application/vnd.ms-excel') {
    return { contentType: mime, kind: 'csv' };
  }
  if (mime === 'text/plain') {
    return { contentType: 'text/plain', kind: 'text' };
  }
  throw new ApiError('Import a .csv or .txt file of email addresses.', 0);
}

/** Turn a failed S3 upload response into a friendly ApiError. */
async function readUploadError(response: Response): Promise<ApiError> {
  let body = '';
  try {
    body = await response.text();
  } catch {
    // Non-text/empty S3 error body; fall through to the status-based message.
  }
  // S3 rejects a file past the presigned policy's content-length-range with an
  // EntityTooLarge error; surface it as the same friendly "too large" the
  // server's import-file 413 uses so both size paths read identically.
  if (response.status === 413 || body.includes('EntityTooLarge')) {
    return new ApiError('That file is too large to import. Please upload a smaller list.', 413);
  }
  return new ApiError('Could not upload that file. Please try again.', response.status);
}

/**
 * Import subscribers from an uploaded CSV/text file. This is a THREE-step flow so
 * the file (subscriber PII + untrusted content) never transits our API:
 *
 *  1. Ask our API for a CONSTRAINED presigned S3 POST for the file's Content-Type.
 *  2. Upload the file STRAIGHT to the private S3 bucket with that presigned POST:
 *     every policy field is appended to the FormData first and the file LAST, with
 *     no auth header and no manual Content-Type (the browser sets the multipart
 *     boundary itself). An S3 rejection (e.g. EntityTooLarge) is surfaced friendly.
 *  3. Call import-file with only the opaque `uploadId` + parse `kind`; the server
 *     reads the object back, parses it SAFELY, imports it with the same importMany
 *     the paste importer uses, and returns the SAME reconcilable breakdown.
 *
 * The unsupported-file-type rejection happens locally (before any request) so the
 * owner gets an instant, friendly error rather than a round-trip failure.
 */
export async function importSubscribersFromFile(
  deps: ApiDeps,
  newsletterId: string,
  file: File,
): Promise<SubscriberImportResult> {
  const { contentType, kind } = resolveUploadType(file);

  // (1) Mint the constrained presigned S3 POST for this exact Content-Type.
  const { uploadId, upload } = await request<SubscriberUploadUrlResponse>(
    deps,
    `/newsletters/${newsletterId}/subscribers/upload-url`,
    { method: 'POST', body: JSON.stringify({ contentType }) },
  );

  // (2) Upload straight to S3. Fields FIRST, file LAST, no auth, no Content-Type.
  const form = new FormData();
  for (const [name, value] of Object.entries(upload.fields)) {
    form.append(name, value);
  }
  form.append('file', file);
  let uploadResponse: Response;
  try {
    uploadResponse = await fetch(upload.url, { method: 'POST', body: form });
  } catch {
    throw new ApiError('Could not upload that file. Check your connection and try again.', 0);
  }
  if (!uploadResponse.ok) {
    throw await readUploadError(uploadResponse);
  }

  // (3) Parse + import the uploaded object, returning the paste importer's shape.
  return request<SubscriberImportResult>(
    deps,
    `/newsletters/${newsletterId}/subscribers/import-file`,
    { method: 'POST', body: JSON.stringify({ uploadId, kind }) },
  );
}

/**
 * `DELETE /newsletters/{id}/subscribers/{subscriberId}`: remove a subscriber from
 * the list (and, server-side, free its email for re-adding). Returns
 * `{ deleted: true }`.
 */
export function removeSubscriber(
  deps: ApiDeps,
  newsletterId: string,
  subscriberId: string,
): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(
    deps,
    `/newsletters/${newsletterId}/subscribers/${subscriberId}`,
    { method: 'DELETE' },
  );
}
